// ============================================================================
// ExercisePicker — a sheet to pick an exercise for a set or cardio entry.
//   • Favorites first (one-tap, the common case) — toggle a star inline.
//   • Search the full catalog via /api/exercises?text= (debounced).
// Wires to useExercises / useFavorites / useToggleFavorite. Returns the chosen
// exercise to the caller. Mobile-first big rows; reduced-motion aware.
// ============================================================================
import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { TextField } from "../../components";
import { IconStar } from "../../components/icons";
import { useExercises, useFavorites, useToggleFavorite } from "../../hooks/exercises";
import type { Exercise } from "../../api";
import type { TFunction } from "i18next";
import { useDebounced } from "./useDebounced";
import { exerciseName } from "./lib";
import { Sheet } from "./Sheet";
import { cn } from "../../lib/cn";

/**
 * Resolve a facet value label (category · equipment · level) via the shared
 * "exercises" i18n namespace, falling back to a title-cased English token so a
 * value the catalog grows into still reads sensibly. Returns "" for blanks.
 */
function facetLabel(
  tEx: TFunction,
  group: "category" | "equipment" | "level",
  raw: string | null | undefined,
): string {
  const value = (raw ?? "").trim();
  if (!value) return "";
  const key = value.toLowerCase();
  const fallback = key.replace(/\b\w/g, (c) => c.toUpperCase());
  return tEx(`values.${group}.${key}`, fallback);
}

interface ExercisePickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (exercise: Exercise) => void;
}

type Tab = "favorites" | "all";

export function ExercisePicker({ open, onClose, onPick }: ExercisePickerProps) {
  const { t, i18n } = useTranslation("sessions");
  const [tab, setTab] = useState<Tab>("favorites");
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 250);

  const favoritesQuery = useFavorites();
  const favorites = useMemo(() => favoritesQuery.data ?? [], [favoritesQuery.data]);

  // Only hit the catalog when on the "all" tab (or actively searching).
  const searching = debounced.trim().length >= 2;
  const listQuery = useExercises(searching ? { text: debounced.trim() } : {});

  const showAll = tab === "all" || searching;
  const rows: Exercise[] = showAll
    ? listQuery.data ?? []
    : favorites;

  const loading = showAll ? listQuery.isLoading : favoritesQuery.isLoading;

  const pick = (ex: Exercise) => {
    onPick(ex);
    setQuery("");
    onClose();
  };

  return (
    <Sheet open={open} onClose={onClose} title={t("picker.title")} size="lg">
      <div className="space-y-4">
        <TextField
          label={t("picker.search")}
          hideLabel
          placeholder={t("picker.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputMode="search"
          iconLeft={
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
              <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
              <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          }
        />

        {!searching && (
          <div className="flex gap-1 rounded-pill bg-surface-sunken p-1">
            <SegBtn active={tab === "favorites"} onClick={() => setTab("favorites")}>
              {t("picker.favorites")}
            </SegBtn>
            <SegBtn active={tab === "all"} onClick={() => setTab("all")}>
              {t("picker.all")}
            </SegBtn>
          </div>
        )}

        {loading ? (
          <p className="py-10 text-center text-sm text-body-soft">
            {t("picker.loading")}
          </p>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-body-soft">
            {showAll ? t("picker.noResults") : t("picker.noFavorites")}
          </p>
        ) : (
          <ul className="space-y-2 pb-2">
            {rows.map((ex, i) => (
              <ExerciseRow
                key={ex.id}
                ex={ex}
                index={i}
                locale={i18n.language}
                isFavorite={favorites.some((f) => f.id === ex.id)}
                onPick={() => pick(ex)}
              />
            ))}
          </ul>
        )}
      </div>
    </Sheet>
  );
}

function SegBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex-1 rounded-pill px-4 py-2 font-display text-sm font-bold transition-all duration-fast",
        active
          ? "bg-surface text-body shadow-xs"
          : "text-body-soft hover:text-body",
      )}
    >
      {children}
    </button>
  );
}

function ExerciseRow({
  ex,
  index,
  locale,
  isFavorite,
  onPick,
}: {
  ex: Exercise;
  index: number;
  locale: string;
  isFavorite: boolean;
  onPick: () => void;
}) {
  const { t } = useTranslation("sessions");
  // Facet value labels (category · equipment · level) live in the shared
  // "exercises" namespace so the picker tags localize to 繁體中文 too.
  const { t: tEx } = useTranslation("exercises");
  const reduce = useReducedMotion();
  const toggleFav = useToggleFavorite();
  const name = exerciseName(ex, locale);

  const tags = [
    facetLabel(tEx, "category", ex.category),
    facetLabel(tEx, "equipment", ex.equipment),
    facetLabel(tEx, "level", ex.level),
  ].filter(Boolean);

  return (
    <motion.li
      initial={reduce ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: reduce ? 0 : Math.min(index * 0.025, 0.25) }}
      className="flex items-center gap-2"
    >
      <button
        type="button"
        onClick={onPick}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line transition-all duration-fast hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-grad-ocean text-white">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
            <path d="M6.5 9v6M17.5 9v6M4 12h2.5M17.5 12H20M6.5 12h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-bold text-body">
            {name || `#${ex.id}`}
          </p>
          {tags.length > 0 && (
            <p className="mt-0.5 truncate text-xs text-body-soft">
              {tags.join(" · ")}
            </p>
          )}
        </div>
      </button>

      <button
        type="button"
        onClick={() => toggleFav.mutate({ id: ex.id, on: !isFavorite })}
        aria-label={t(isFavorite ? "picker.unfavorite" : "picker.favorite")}
        aria-pressed={isFavorite}
        className={cn(
          "grid h-11 w-11 shrink-0 place-items-center rounded-md ring-1 ring-line transition-colors",
          isFavorite ? "text-amber" : "text-body-faint hover:text-amber",
        )}
      >
        <IconStar width={20} height={20} filled={isFavorite} />
      </button>
    </motion.li>
  );
}
